import { AxiosError } from 'axios';

import { apiService } from '../../../shared/services/apiServices';
import {
  EQUIPMENT_ERROR_MESSAGES,
  FACILITY_VIEW_ERROR_MESSAGES,
} from '../errors';
import { EQUIPMENT_ERROR_MESSAGES as TL } from '../errors/translations/errorMessages';
import type { MediaResource } from '../types/addEquipmentType';
import equipmentEndpoints from './equipmentEndpoints';

export interface AddEquipmentResult {
  success: boolean;
  data?: unknown;
  error?: string;
  fieldErrors?: { serialNumber?: string };
  equipmentId?: string;
}

interface CreateEquipmentResponse {
  equipment: {
    id: string;
    equipmentId: string;
  };
}

const ERROR_MESSAGES = EQUIPMENT_ERROR_MESSAGES;

const extractErrorMessage = (errorData: unknown): string => {
  if (!errorData) {
    return ERROR_MESSAGES.DEFAULT_ERROR;
  }

  if (typeof errorData === 'string') {
    return errorData;
  }

  if (typeof errorData === 'object') {
    const apiError = errorData as {
      message?: string;
      error?: string;
      details?: Array<{
        path?: string;
        message?: string;
        code?: string;
      }>;
    };

    // Handle filename validation errors from the backend
    if (apiError.error === 'Input Validation Failed' && apiError.details) {
      const filenameError = apiError.details.find(
        (detail) =>
          (detail.path === 'image.filename' ||
            detail.path?.startsWith('docs[')) &&
          detail.message === 'Invalid characters in filename'
      );
      if (filenameError) {
        return ERROR_MESSAGES.INVALID_FILENAME;
      }
    }

    const errorMessage = apiError.message || apiError.error;

    if (errorMessage === 'Internal Server Error') {
      return ERROR_MESSAGES.UNEXPECTED_ERROR;
    }

    if (errorMessage?.startsWith('Access denied')) {
      return ERROR_MESSAGES.ACCESS_DENIED;
    }

    return errorMessage || ERROR_MESSAGES.DEFAULT_ERROR;
  }

  return ERROR_MESSAGES.DEFAULT_ERROR;
};

export const addEquipment = async (
  facilityId: string,
  clientId: string,
  entityId: string | undefined,
  name: string,
  description: string,
  category: string,
  serialNumber?: string,
  dateOfProduction?: string,
  modelName?: string,
  image?: MediaResource,
  docs?: MediaResource[]
): Promise<AddEquipmentResult> => {
  try {
    const requestBody: {
      id?: string;
      facilityId: string;
      clientId: string;
      name: string;
      description: string;
      categoryId: string;
      serialNumber?: string;
      dateOfProduction?: string;
      modelName?: string;
      image?: MediaResource;
      docs?: MediaResource[];
    } = {
      facilityId,
      clientId,
      name: name.trim(),
      description: description.trim(),
      categoryId: category,
    };

    if (entityId) {
      requestBody.id = entityId;
    }

    if (serialNumber) {
      requestBody.serialNumber = serialNumber.trim();
    }

    if (dateOfProduction) {
      requestBody.dateOfProduction = dateOfProduction;
    }

    if (modelName) {
      requestBody.modelName = modelName.trim();
    }

    if (image) {
      requestBody.image = image;
    }

    if (docs && docs.length > 0) {
      requestBody.docs = docs;
    }

    const createResult = await apiService.postAPI(
      equipmentEndpoints.EQUIPMENT_DRAFT,
      requestBody,
      { skipGlobalErrorRedirect: true }
    );

    if (createResult.error) {
      if (createResult.status === 403) {
        return {
          success: false,
          error: FACILITY_VIEW_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP,
        };
      }
      if (createResult.status === 409) {
        return {
          success: false,
          fieldErrors: {
            serialNumber: TL.SERIAL_NUMBER_DUPLICATE.polish,
          },
        };
      }
      const errorMessage = extractErrorMessage(createResult.data);
      return {
        success: false,
        error: createResult.isNetworkError
          ? ERROR_MESSAGES.NETWORK_ERROR
          : errorMessage,
      };
    }

    const responseData = createResult.data as CreateEquipmentResponse;
    return {
      success: true,
      data: responseData,
      equipmentId: responseData.equipment.equipmentId,
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      const isNetworkError = !error.response && !!error.request;
      if (isNetworkError) {
        return { success: false, error: ERROR_MESSAGES.NETWORK_ERROR };
      }
      const errorMessage = extractErrorMessage(error.response?.data);
      return { success: false, error: errorMessage };
    }

    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};
